import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  addDirectRoom as matrixAddDirectRoom,
  addDirectRooms as matrixAddDirectRooms,
  clearSaved as matrixClearSaved,
  createClient,
  addSpaceChild as matrixAddSpaceChild,
  createRoom as matrixCreateRoom,
  findDirectRoom as matrixFindDirectRoom,
  getUserId as matrixGetUserId,
  getDeviceId as matrixGetDeviceId,
  getOwnDevices as matrixGetOwnDevices,
  isInvalidGrantError,
  isUnknownTokenError,
  isStaleCryptoStoreError,
  leaveRoom as matrixLeaveRoom,
  logout,
  moveRoomToSpace as matrixMoveRoomToSpace,
  saveEvent as matrixSaveEvent,
  searchRooms as matrixSearchRooms,
  searchUserDirectory as matrixSearchUserDirectory,
  sendReadReceipt as matrixSendReadReceipt,
  sendThreadReply as matrixSendThreadReply,
  startOidcLogin as matrixStartOidcLogin,
  stopSync,
  storeNameFromSession,
  subscribeSessionBroadcast,
  subscribeMentionsFeed as matrixSubscribeMentionsFeed,
  subscribeThread as matrixSubscribeThread,
  subscribeUnreadTimeline as matrixSubscribeUnreadTimeline,
  tryRestoreSession,
  unsaveEvent as matrixUnsaveEvent,
  type Client,
  type ClientState,
  type CreateRoomOptions,
  type IAuthData,
  type MentionFeedItem,
  type RoomSearchResult,
  type RoomSummary,
  type ThreadReplyOptions,
  type RecoveryDevice,
  type RecoveryPrimaryAction,
  type RecoveryViewModel,
  type SaveMutationResult,
  type SavedItem,
  type ThreadSubscriber,
  type TimelineEvent,
  type UnreadGroup,
  type Unsubscribe,
  type UserDirectoryEntry,
  subscribeBlockedUsers,
  withoutBlockedDirects,
} from "~/lib/matrix";
import {
  bootstrapCrypto,
  runOidcLogin,
  runPostLoginPipeline,
  teardownSub,
  toCryptoBootstrapError,
  type CryptoBootstrapError,
  type CryptoBootstrapStage,
} from "~/lib/post-login-pipeline";
import { setChannelType as matrixSetChannelType, type ChannelType } from "~/lib/channel-type";
import { clearAvatarMedia } from "~/lib/avatar-media";
import {
  deleteChatIndexedDbDatabases,
  getCallbackUrl,
  detectLegacySnapshot,
  isKeychainTransientError,
  isKeychainUnavailableError,
  MATRIX_SESSION_STORE_PREFIX,
  authenticateInApp,
  openExternal,
  platform,
  readSecret,
  refreshStrongholdSnapshotKey,
  relaunchApp,
  takeLaunchOidcCallback,
  wipeDesktopSecrets,
} from "~/lib/platform";
import { captureSdkException, recordSdkBreadcrumb } from "~/lib/telemetry";
import { markBoot } from "~/lib/boot-timing";
import { startScheduledDispatcher } from "~/lib/scheduled-messages";
import {
  isRoomVisibleInSpaceSelection,
  isUnparentedRoomVisible,
  repairSpaceSelection,
  unparentedVisibility,
  visibleRoomIdsForSpace,
} from "~/lib/spaces";
import { isDmRoom } from "~/lib/room-utils";
import { useEmbedRuntime } from "~/context/EmbedRuntimeContext";
import { UiaDialog, type UiaDialogFlow } from "~/components/UiaDialog";

const MATRIX_STORE_PREFIX = `${MATRIX_SESSION_STORE_PREFIX}-`;

interface PendingUiaRequest {
  flow: UiaDialogFlow;
  onSubmit: (password: string) => void | Promise<void>;
  onOpen?: () => void;
  onCancel: () => void;
}

const OAUTH_UIA_STAGES = new Set(["org.matrix.cross_signing_reset", "org.matrix.msc2967.oauth2"]);

function uiaStages(req: IAuthData): string[] {
  return Array.from(new Set(req.flows?.flatMap((flow) => flow.stages) ?? []));
}

function hasSinglePasswordFlow(req: IAuthData): boolean {
  return (
    req.flows?.some((flow) => flow.stages.length === 1 && flow.stages[0] === "m.login.password") ??
    false
  );
}

function oauthApprovalUrl(req: IAuthData): string | null {
  for (const flow of req.flows ?? []) {
    if (flow.stages.length !== 1) continue;
    const [stage] = flow.stages;
    if (!stage) continue;
    if (!OAUTH_UIA_STAGES.has(stage)) continue;
    const params = req.params?.[stage];
    if (!params || typeof params !== "object") continue;
    const url = "url" in params ? params.url : undefined;
    if (typeof url === "string" && url.length > 0) return url;
  }
  return null;
}

/**
 * Which navigation "view" the main area is rendering.
 *
 * - `"room"` — timeline for `activeRoomId`.
 * - `"saved"` — the Saved view (#1869).
 * - `"unreads"` — the All Unreads activity view (#1866).
 * - `"mentions"` — mentions activity view (#1867).
 * - `"threads"` — participated threads activity view (#1868).
 * - `"scheduled"` — queued scheduled sends (#1865).
 * - `"archived"` — archived channels.
 *
 * Future activity views (mentions, threads overview) slot in here.
 */
export type ActiveView =
  | "room"
  | "saved"
  | "unreads"
  | "mentions"
  | "threads"
  | "scheduled"
  | "archived";

/**
 * Which thread is currently open in the right panel. Single-slot
 * (not per-room): switching rooms closes the open thread, by design
 * — the brief frames the panel as a quiet side-room, not a per-room
 * tab strip. Storing the full root event here avoids a separate
 * `getEvent` SDK lookup at render time; the opener (the `Timeline`)
 * already has the event in scope.
 */
export interface ActiveThreadRoot {
  roomId: string;
  root: TimelineEvent;
}

/** A saved event the user has asked us to jump to. The timeline reads
 *  this, scrolls to the event, fires the highlight flash, and then
 *  calls `clearPendingJump` so a re-render doesn't re-fire. */
export interface PendingJump {
  roomId: string;
  eventId: string;
  /** Changes on every jump request so repeated jumps to the same event
   *  still trigger the highlight — a plain object would be reference-
   *  equal across renders if the room+event pair hasn't changed. */
  token: number;
}

export interface MatrixContextValue {
  state: ClientState;
  lastError?: unknown;
  displayName: string | null;
  /** Matrix user id of the signed-in account, or null before login. The
   *  ownership checks in `MessageActions` and friends read this to
   *  decide whether Edit/Delete show at all. */
  userId: string | null;
  /**
   * The active Matrix `Client` once login completes. Exposed so hooks
   * can call into `matrix.ts` verbs directly (`useTimeline`,
   * `useMessageActions`, and future feature hooks). Components should
   * prefer higher-level verbs on this context where one exists.
   */
  client: Client | null;
  /** True once the active sync has delivered a room snapshot. The shell
   *  gate holds the syncing screen while this is false, so a reload
   *  never flashes the empty "No rooms yet" sidebar between sync and the
   *  first room snapshot. */
  roomsReady: boolean;
  /** The root space selected in the TopBar. Controls which sub-spaces
   *  are shown in the SpaceRail. */
  activeOrgSpaceId: string | null;
  /** Switch to a different root space (TopBar). Resets `activeSpaceId`
   *  to the new root space so the SpaceRail highlights it. */
  setActiveOrgSpaceId: (id: string | null) => void;
  /** The space selected in the SpaceRail. Controls which rooms are
   *  shown in the sidebar. Can be the root space itself or a sub-space. */
  activeSpaceId: string | null;
  /** Switch to a different space in the SpaceRail. Clears
   *  `activeRoomId`. */
  setActiveSpaceId: (id: string | null) => void;
  activeRoomId: string | null;
  setActiveRoomId: (id: string | null) => void;
  /** Which view the main column is rendering. */
  activeView: ActiveView;
  /** Switch the main view. Does not change `activeRoomId` — the timeline
   *  remembers the last room so closing Saved returns the user to it. */
  setActiveView: (view: ActiveView) => void;
  /** All saved items. */
  savedItems: SavedItem[];
  /**
   * Save a message in the active room. Resolves with the
   * mutation result (final list + how many items were evicted so
   * callers can toast).
   */
  saveEvent: (params: { roomId: string; eventId: string }) => Promise<SaveMutationResult>;
  /** Remove a saved message. Idempotent. */
  unsaveEvent: (eventId: string) => Promise<void>;
  /** Clear every saved item. */
  clearSaved: () => Promise<void>;
  /**
   * Live unread digest grouped by room. The Unreads activity view reads this.
   */
  unreadGroups: UnreadGroup[];
  /** Live mentions + reactions feed. */
  mentionsFeed?: MentionFeedItem[];
  /** Mark the currently visible mentions feed read and clear it locally. */
  clearMentions?: (items?: MentionFeedItem[]) => Promise<void>;
  /**
   * Ask the app shell to navigate to `roomId` and scroll to `eventId`
   * with a highlight flash. Used by the Saved view's click-to-jump.
   * Sets `activeRoomId`, flips `activeView` to `"room"`, and publishes
   * a `pendingJump` the timeline consumes.
   */
  jumpToEvent: (params: { roomId: string; eventId: string }) => void;
  /** The next pending jump the timeline should consume, or null. */
  pendingJump: PendingJump | null;
  /** Called by the timeline after it has applied the jump. */
  clearPendingJump: () => void;
  /**
   * Whether crypto has initialised on this boot. Encrypted rooms
   * render a loading state until this flips to true; unencrypted
   * rooms render immediately. Mirrors the ticket's UI-gating rule.
   */
  cryptoReady: boolean;
  /** Non-fatal E2EE bootstrap failure. Unencrypted rooms remain usable. */
  cryptoError: CryptoBootstrapError | null;
  forceReloginBanner?: boolean;
  keychainError?: unknown;
  retryKeychain?: () => Promise<void>;
  /** Re-run the E2EE bootstrap sequence for the current session. */
  retryCryptoBootstrap: (options?: { skipRecoveryGate?: boolean }) => Promise<void>;
  /**
   * True when a key backup exists on the server but this device
   * doesn't have the secret storage key yet — the UI should prompt
   * for recovery. False when we've already unlocked or there's no
   * backup to restore.
   */
  needsRecovery: boolean;
  recoveryView: RecoveryViewModel | null;
  /**
   * The recovery key produced by first-login bootstrap, shown once
   * and then cleared via `dismissRecoveryKey`. Null when we're not
   * in the first-run flow.
   */
  pendingRecoveryKey: string | null;
  /** Clear `pendingRecoveryKey` after the user confirms they've saved it. */
  dismissRecoveryKey: () => void;
  /** Mark the device recovered so the restore prompt disappears. */
  markRecovered: () => void;
  skipRecoveryForSession: () => void;
  /** This device still lacks the keys for older encrypted history, even if
   *  the user dismissed the unlock prompt with "Not now". */
  canUnlockHistory: boolean;
  /** Reopen the unlock prompt after "Not now" (e.g. from a locked run). */
  resumeRecovery: () => void;
  signIn: () => Promise<void> | void;
  signOut: () => Promise<void>;
  /** Register a teardown that runs while the client is still authenticated,
   *  just before `logout()` — the delete-pusher call needs an authed client. */
  registerSignOutTeardown: (teardown: () => Promise<void> | void) => () => void;
  recoverToLogin: () => Promise<void>;
  /**
   * Complete a MAS / OIDC round-trip with the redirect URI's raw
   * query string (everything after the leading `?`). On Tauri this is
   * invoked from the deeplink listener; on web from the `/oidc/callback`
   * route handler. Safe to call before boot completes — the callback
   * query is queued and replayed once the Matrix client is ready.
   */
  completeLogin: (callbackQuery: string) => Promise<void>;
  /** Create a room and optionally attach it to a Matrix Space. Returns the room id. */
  createRoom: (opts: CreateRoomOptions & { parentSpaceId?: string | null }) => Promise<string>;
  /**
   * Create a Matrix Space (room with `type: "m.space"`). If `parentSpaceId` is given, the new space is
   * added as a child of the parent via `m.space.child`. Returns the
   * new space's room id.
   */
  createSpace: (opts: {
    name: string;
    topic?: string;
    parentSpaceId?: string;
    isPrivate?: boolean;
  }) => Promise<string>;
  moveRoomToSpace: (opts: { roomId: string; spaceId: string | null }) => Promise<void>;
  /**
   * Record a newly-created DM in `m.direct` so future `findDirectRoom`
   * lookups find it on every device. Wraps the account-data merge;
   * callers never touch the raw event.
   */
  addDirectRoom: (userId: string, roomId: string) => Promise<void>;
  createDirectRoom: (opts: { userIds: string[] }) => Promise<string>;
  /** Find a 1:1 DM (any workspace) by peer, or `null`. */
  findDirectRoom: (userId: string) => RoomSummary | null;
  /**
   * Search the homeserver user directory. Thin wrapper over
   * `matrix.searchUserDirectory` so the dialogs don't need to reach
   * through to `client.bindings`. Returns `[]` when no client.
   */
  searchUserDirectory: (term: string) => Promise<UserDirectoryEntry[]>;
  /**
   * Search the homeserver public-rooms directory. Thin wrapper over `matrix.searchRooms`.
   */
  searchRooms: (term: string) => Promise<RoomSearchResult[]>;
  /**
   * Write the `com.nurion.channel_type` state event for a room. Used
   * by the sidebar's "Take over" affordance on agent-only rows; a
   * write from a member without admin power level surfaces as a
   * Matrix permission error. The caller is responsible for catching
   * the rejection — the sidebar logs + swallows today and a follow-up
   * will route it through `ToastContext`. The subscription delivers
   * the new `channelType` value to every consumer once the SDK
   * acknowledges the write.
   */
  setChannelType: (roomId: string, type: ChannelType) => Promise<void>;
  /** Leave a joined room, clear active room state, and return to room view. */
  leaveRoom: (roomId: string) => Promise<void>;
  /**
   * Subscribe to the live list of thread replies for `rootEventId` in
   * `roomId`. Returns a no-op unsubscribe while the client is still
   * booting so consumers don't need a separate "not ready" branch.
   */
  subscribeThread: (roomId: string, rootEventId: string, cb: ThreadSubscriber) => Unsubscribe;
  /**
   * Send a reply into a thread. Throws if the client is not ready.
   * `latestReplyId` is the most recent event id in the thread; when
   * the thread has no replies yet callers may omit it and the root
   * event id will be used.
   */
  sendThreadReply: (
    roomId: string,
    rootEventId: string,
    body: string,
    latestReplyId?: string,
    options?: ThreadReplyOptions,
  ) => Promise<string>;
  /**
   * The thread root the right panel should render, or `null` when
   * no thread is open. Single-slot — switching rooms closes the open
   * thread; the opener stores the full event so the panel can render
   * the root block without an additional SDK lookup.
   */
  activeThreadRoot: ActiveThreadRoot | null;
  /** Open the thread panel anchored on `root` in `roomId`. Replaces
   *  any currently-open thread (single-slot). */
  openThread: (params: { roomId: string; root: TimelineEvent }) => void;
  /** Close the thread panel. No-op when nothing is open. */
  closeThread: () => void;
}

// Exported so Storybook stories can inject a lightweight stand-in value
// without booting the full Matrix client. Production code must not touch
// this — always go through `useMatrix()`.
export const MatrixContext = createContext<MatrixContextValue | null>(null);

export function useMatrix(): MatrixContextValue {
  const value = useContext(MatrixContext);
  if (!value) {
    throw new Error("useMatrix must be used within a <MatrixProvider>");
  }
  return value;
}

export const MatrixRoomsContext = createContext<RoomSummary[] | null>(null);

export function useMatrixRooms(): RoomSummary[] {
  const rooms = useContext(MatrixRoomsContext);
  if (rooms === null) throw new Error("useMatrixRooms must be used inside MatrixProvider");
  return rooms;
}

/**
 * MatrixProvider props.
 *
 * The standalone app (App.tsx) renders `<MatrixProvider>` with no
 * client and lets the provider boot via SSO restore / login. The embed
 * surface (`EmbeddableChatProvider`) hands a pre-authenticated client
 * built by `createClientFromToken` — the provider then skips the SSO
 * boot path and treats the client as if it were a restored session,
 * running crypto bootstrap + `startSync` + the live subscriptions.
 *
 * `preAuthedClient` may flip from null to non-null exactly once. The
 * provider does not unwind a previous embed boot to swap to a
 * different token; embedders that need to re-key the session must
 * unmount + remount the provider with the new client. This keeps the
 * subscription / sync lifecycle symmetric with the SSO flow without
 * a separate "swap client" path.
 */
export interface MatrixProviderProps {
  children: ReactNode;
  /** Pre-authed client built by `createClientFromToken`. When set,
   *  the provider skips SSO and uses this client for all calls. */
  preAuthedClient?: Client | null;
  /** Namespace for persisted space navigation. Null disables persistence. */
  navigationStorageScope?: string | null;
  autoSelectRoom?: boolean;
}

const DEFAULT_NAVIGATION_STORAGE_SCOPE = "standalone";
const LEGACY_NAVIGATION_ORG_KEY = "nurion-chat:activeOrgSpaceId";
const LEGACY_NAVIGATION_SPACE_KEY = "nurion-chat:activeSpaceId";

// Persisted (not per tab) so a relaunched phone app doesn't ask again;
// sign-out clears it, and locked messages keep their inline Unlock.
function recoverySkipKey(userId: string): string {
  return `chat.recovery.skipped:${userId}`;
}

function readRecoverySkip(userId: string | null): boolean {
  if (!userId || typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(recoverySkipKey(userId)) === "1";
  } catch {
    return false;
  }
}

function writeRecoverySkip(userId: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(recoverySkipKey(userId), "1");
  } catch {
    return;
  }
}

function clearRecoverySkip(userId: string | null): void {
  if (!userId || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(recoverySkipKey(userId));
  } catch {
    return;
  }
}

function deviceLabel(device: { displayName?: string | null; deviceId: string }): string {
  return device.displayName?.trim() || device.deviceId;
}

const STALE_MS = 30 * 24 * 60 * 60 * 1000;

function isRecoveryDeviceStale(device: RecoveryDevice, now: number): boolean {
  if (device.lastSeenTs == null) return false;
  return now - device.lastSeenTs > STALE_MS;
}

function compareRecoveryDevices(a: RecoveryDevice, b: RecoveryDevice): number {
  const aNamed = a.label !== a.deviceId;
  const bNamed = b.label !== b.deviceId;
  if (aNamed !== bNamed) return aNamed ? -1 : 1;
  if (a.lastSeenTs != null && b.lastSeenTs == null) return -1;
  if (a.lastSeenTs == null && b.lastSeenTs != null) return 1;
  if (a.lastSeenTs != null && b.lastSeenTs != null && a.lastSeenTs !== b.lastSeenTs) {
    return b.lastSeenTs - a.lastSeenTs;
  }
  return a.label.localeCompare(b.label) || a.deviceId.localeCompare(b.deviceId);
}

function canUseDeviceForRecovery(device: RecoveryDevice, currentDeviceId: string | null): boolean {
  if (device.deviceId === currentDeviceId) return false;
  if (!device.isCrossSignedByOwner) return false;
  if (device.hasIdentityKey === false) return false;
  return device.isDehydrated !== true;
}

export function buildRecoveryViewModel(opts: {
  devices: RecoveryDevice[];
  skipped: boolean;
  now?: number;
}): RecoveryViewModel {
  const now = opts.now ?? Date.now();
  const sortedDevices = opts.devices.slice().sort(compareRecoveryDevices);
  const recent = sortedDevices.filter((device) => !isRecoveryDeviceStale(device, now));
  const stale = sortedDevices.filter((device) => isRecoveryDeviceStale(device, now));
  const primaryDevice = recent[0] ?? sortedDevices[0];
  const primaryAction: RecoveryPrimaryAction = primaryDevice
    ? { kind: "verify-device", device: primaryDevice }
    : { kind: "recovery-key" };
  return {
    primaryAction,
    devices: recent,
    staleDevices: stale,
    canShowQr: sortedDevices.length > 0,
    canUseEmoji: sortedDevices.length > 0,
    skipped: opts.skipped,
  };
}

function navigationStorageKey(scope: string, field: "org" | "child"): string {
  return `nurion-chat:${scope}:space-navigation:${field}`;
}

function readNavigationSelection(scope: string | null): {
  orgSpaceId: string | null;
  childSpaceId: string | null;
} {
  if (!scope || typeof localStorage === "undefined")
    return { orgSpaceId: null, childSpaceId: null };
  try {
    const orgSpaceId = localStorage.getItem(navigationStorageKey(scope, "org"));
    const childSpaceId = localStorage.getItem(navigationStorageKey(scope, "child"));
    if (orgSpaceId || childSpaceId || scope !== DEFAULT_NAVIGATION_STORAGE_SCOPE) {
      return { orgSpaceId, childSpaceId };
    }

    const legacyOrgSpaceId = localStorage.getItem(LEGACY_NAVIGATION_ORG_KEY);
    const legacySpaceId = localStorage.getItem(LEGACY_NAVIGATION_SPACE_KEY);
    return {
      orgSpaceId: legacyOrgSpaceId,
      childSpaceId: legacySpaceId && legacySpaceId !== legacyOrgSpaceId ? legacySpaceId : null,
    };
  } catch {
    return { orgSpaceId: null, childSpaceId: null };
  }
}

function navigationSelectionNeedsWrite(
  scope: string | null,
  orgSpaceId: string | null,
  childSpaceId: string | null,
): boolean {
  if (!scope || typeof localStorage === "undefined") return false;
  try {
    if (localStorage.getItem(navigationStorageKey(scope, "org")) !== orgSpaceId) return true;
    if (localStorage.getItem(navigationStorageKey(scope, "child")) !== childSpaceId) return true;
    if (scope !== DEFAULT_NAVIGATION_STORAGE_SCOPE) return false;
    return (
      localStorage.getItem(LEGACY_NAVIGATION_ORG_KEY) !== null ||
      localStorage.getItem(LEGACY_NAVIGATION_SPACE_KEY) !== null
    );
  } catch {
    return false;
  }
}

function writeNavigationSelection(
  scope: string | null,
  orgSpaceId: string | null,
  childSpaceId: string | null,
): void {
  if (!scope || typeof localStorage === "undefined") return;
  try {
    if (orgSpaceId) localStorage.setItem(navigationStorageKey(scope, "org"), orgSpaceId);
    else localStorage.removeItem(navigationStorageKey(scope, "org"));
    if (childSpaceId) localStorage.setItem(navigationStorageKey(scope, "child"), childSpaceId);
    else localStorage.removeItem(navigationStorageKey(scope, "child"));
    if (scope === DEFAULT_NAVIGATION_STORAGE_SCOPE) {
      localStorage.removeItem(LEGACY_NAVIGATION_ORG_KEY);
      localStorage.removeItem(LEGACY_NAVIGATION_SPACE_KEY);
    }
  } catch {
    // Quota / disabled storage — navigation persistence is best-effort.
  }
}

type ActiveRoomOrigin = "auto" | "user" | null;

function lastRoomStorageKey(scope: string | null, userId: string): string | null {
  if (!scope) return null;
  return `nurion-chat:${scope}:last-room:${userId}`;
}

function readLastRoomsBySpace(scope: string | null, userId: string | null): Record<string, string> {
  if (!userId || typeof localStorage === "undefined") return {};
  const key = lastRoomStorageKey(scope, userId);
  if (!key) return {};
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const entries = Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    );
    return Object.fromEntries(entries);
  } catch {
    return {};
  }
}

function writeLastRoomForSpace(
  scope: string | null,
  userId: string | null,
  lastRoomsBySpace: Record<string, string>,
  spaceId: string,
  roomId: string,
): Record<string, string> {
  const next = { ...lastRoomsBySpace, [spaceId]: roomId };
  if (!userId || typeof localStorage === "undefined") return next;
  const key = lastRoomStorageKey(scope, userId);
  if (!key) return next;
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // Quota / disabled storage — last-room persistence is best-effort.
  }
  return next;
}

function isValidRoomForSpace(
  rooms: RoomSummary[],
  spaceId: string | null,
  roomId: string,
): boolean {
  const room = rooms.find((candidate) => candidate.id === roomId);
  if (!room || room.isSpace || room.archived !== null) return false;
  if (visibleRoomIdsForSpace(rooms, spaceId).has(roomId)) return true;
  return isUnparentedRoomVisible(rooms, spaceId, roomId);
}

function pickRoomForSpace(
  rooms: RoomSummary[],
  spaceId: string | null,
  lastRoomsBySpace: Record<string, string>,
): string | null {
  if (!spaceId) return null;
  const visibleIds = visibleRoomIdsForSpace(rooms, spaceId);
  const { claimed, show } = unparentedVisibility(rooms, spaceId);
  const isVisible = (room: RoomSummary): boolean =>
    visibleIds.has(room.id) || (show && !room.isDirect && !claimed.has(room.id));
  const persistedRoomId = lastRoomsBySpace[spaceId];
  const persistedRoom = persistedRoomId
    ? rooms.find((candidate) => candidate.id === persistedRoomId)
    : null;
  if (
    persistedRoom &&
    !persistedRoom.isSpace &&
    persistedRoom.archived === null &&
    isVisible(persistedRoom)
  ) {
    return persistedRoom.id;
  }

  let mostRecent: RoomSummary | null = null;
  for (const room of rooms) {
    if (room.isSpace || room.archived !== null || !isVisible(room)) continue;
    if (!mostRecent || room.lastActivityTs > mostRecent.lastActivityTs) mostRecent = room;
  }
  return mostRecent?.id ?? null;
}

interface PreparedLogin {
  client: Client;
  preparedAt: number;
  pending: Promise<{ url: string; state: string }>;
}

const PREPARED_LOGIN_TTL_MS = 10 * 60_000;

export function MatrixProvider({
  children,
  preAuthedClient = null,
  navigationStorageScope = DEFAULT_NAVIGATION_STORAGE_SCOPE,
  autoSelectRoom = true,
}: MatrixProviderProps) {
  const { homeserverUrl } = useEmbedRuntime();
  const [state, setState] = useState<ClientState>("loading");
  const [lastError, setLastError] = useState<unknown>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  // Matrix user id is read off the bindings once at login and mirrored
  // into state so React can re-render the hover action bar (Edit /
  // Delete visibility) as soon as the session lands. Keeping this out of
  // the ref world also makes Storybook mocking trivial — stories inject
  // `userId` directly through the fake context value.
  const [userId, setUserId] = useState<string | null>(null);
  // Mirrors `clientRef` into state so consumers of `useMatrix()` that
  // need the handle (e.g. `useMessageActions`) re-render when a session
  // lands. The ref stays as the source of truth for effects that have
  // to read synchronously (boot, signOut) — two mirrors, one purpose.
  const [client, setClient] = useState<Client | null>(null);
  const [rooms, setRooms] = useState<RoomSummary[]>([]);
  const [roomsReady, setRoomsReady] = useState(false);
  const [initialNavigationSelection] = useState(() =>
    readNavigationSelection(navigationStorageScope),
  );
  const [activeOrgSpaceId, setActiveOrgSpaceIdState] = useState<string | null>(
    initialNavigationSelection.orgSpaceId,
  );
  const [activeSpaceId, setActiveSpaceIdState] = useState<string | null>(
    initialNavigationSelection.orgSpaceId,
  );
  const [activeRoomId, setActiveRoomIdState] = useState<string | null>(null);
  const [activeView, setActiveViewState] = useState<ActiveView>("room");
  const [savedItems, setSavedItems] = useState<SavedItem[]>([]);
  const [unreadGroups, setUnreadGroups] = useState<UnreadGroup[]>([]);
  const [mentionsFeed, setMentionsFeed] = useState<MentionFeedItem[]>([]);
  const [pendingJump, setPendingJump] = useState<PendingJump | null>(null);
  const [activeThreadRoot, setActiveThreadRoot] = useState<ActiveThreadRoot | null>(null);
  const [cryptoReady, setCryptoReady] = useState(false);
  const [cryptoError, setCryptoErrorState] = useState<CryptoBootstrapError | null>(null);
  const [forceReloginBanner, setForceReloginBanner] = useState(false);
  const [keychainError, setKeychainError] = useState<unknown>(null);
  const [bootRetryToken, setBootRetryToken] = useState(0);
  const [needsRecovery, setNeedsRecovery] = useState(false);
  const [skippedRecovery, setSkippedRecovery] = useState(false);
  const [recoveryDevices, setRecoveryDevices] = useState<RecoveryDevice[]>([]);
  const [pendingRecoveryKey, setPendingRecoveryKey] = useState<string | null>(null);
  const [pendingUiaRequest, setPendingUiaRequest] = useState<PendingUiaRequest | null>(null);
  const clientRef = useRef<Client | null>(null);
  const pendingDirectRoomsRef = useRef<
    Map<string, { roomId: string | null; complete: boolean; inFlight: Promise<string> | null }>
  >(new Map());
  const directWriteRef = useRef<Promise<void>>(Promise.resolve());
  const pendingUiaRejectRef = useRef<((err: Error) => void) | null>(null);
  const unknownUiaCapturedRef = useRef(false);
  // Callback queries arriving before the client exists are queued and
  // replayed after boot. A single slot is enough — a second callback
  // while one is already queued is almost certainly a bug, not a
  // feature.
  const pendingCallbackRef = useRef<string | null>(null);
  // An OIDC callback can reach the webview twice on mobile: the live
  // `oidc-callback` event and the cold-start buffer drain. Both carry the
  // same query, and an authorization code can only be redeemed once, so we
  // dedupe by exact query string before driving the login.
  const handledOidcQueriesRef = useRef<Set<string>>(new Set());
  const syncActiveRef = useRef<boolean>(false);
  // The room subscription is created once per boot and torn down on
  // unmount / logout. Keeping the unsub outside the effect lets us tear
  // it down from `signOut` too.
  const roomsUnsubRef = useRef<(() => void) | null>(null);
  // Saved-items subscription follows the same pattern. Lives across the
  // lifetime of a signed-in session; torn down on logout so a second
  // user on the same device doesn't inherit the previous user's list.
  const savedUnsubRef = useRef<(() => void) | null>(null);
  const scheduledDispatcherStopRef = useRef<(() => void) | null>(null);
  // True between mount and unmount. The unmount cleanup flips it to
  // false so any in-flight pipeline (boot effect or `completeLogin`)
  // bails before it writes into a torn-down React tree. Without this
  // an OIDC callback that fires during component unmount would still
  // run `setRooms` / `setUserId` once it returns from the awaited
  // `getDisplayName`.
  const mountedRef = useRef<boolean>(true);
  // Generation counter incremented on every pipeline entry. The
  // `isCancelled` predicate the pipeline sees compares the
  // generation it captured at entry against the current value — a
  // newer generation means a superseding pipeline (e.g. a re-login
  // while a restore-path pipeline is mid-flight) and the older one
  // must stop writing into shared state. This closes the TOCTOU
  // window on the SSO branch where two pipelines could race against
  // the same `setRooms` / identity setters after the
  // `syncActiveRef = false` reset.
  const pipelineGenRef = useRef<number>(0);
  const retryCryptoRef = useRef(false);
  // Jump tokens are monotonically increasing integers so repeated jumps
  // to the same event still flip reference equality for `pendingJump`.
  const jumpTokenRef = useRef(0);
  // Tracks the most recently-applied active room id outside React's
  // state machine so room-switch helpers can compare against the
  // previous value without doing the comparison inside a state
  // updater (React's contract requires updaters to be pure — no side
  // effects, no foreign setState calls; Strict Mode runs them twice,
  // concurrent rendering may invoke them at unexpected times). Both
  // `setActiveRoomId` and `jumpToEvent` close the open thread when
  // the room actually changes; the ref keeps that decision outside
  // the updater body, and the org-switch / signOut clear paths reset
  // it so a re-login or org-flip starts from a clean baseline.
  const activeRoomIdRef = useRef<string | null>(null);
  const activeRoomOriginRef = useRef<ActiveRoomOrigin>(null);
  const activeSpaceIdRef = useRef<string | null>(activeSpaceId);
  const roomsRef = useRef<RoomSummary[]>(rooms);
  const userIdRef = useRef<string | null>(userId);
  const lastRoomsBySpaceRef = useRef<Record<string, string>>({});
  const roomsReceivedRef = useRef(false);
  const syncedRef = useRef(false);
  const syncingRef = useRef(false);
  // Latest room-snapshot count. While > 0 the `syncing` transition must NOT
  // clear the gate, so a relaunch opens the shell from cached rooms before
  // the network sync completes.
  const currentRoomCountRef = useRef(0);
  const resetRoomsGateOnSyncing = useCallback(() => {
    // Close synchronously (not from a `[state]` effect) so a batched
    // syncing→synced render can't skip it and let an empty pre-sync
    // snapshot open the shell. Cached rooms (count > 0) keep it open.
    if (currentRoomCountRef.current === 0) {
      roomsReceivedRef.current = false;
      setRoomsReady(false);
    }
  }, []);
  const [lastRoomsVersion, setLastRoomsVersion] = useState(0);
  const clearedMentionIdsRef = useRef<Set<string>>(new Set());
  const clearedMentionWatermarksRef = useRef<Map<string, number>>(new Map());
  const signOutTeardownsRef = useRef<Set<() => Promise<void> | void>>(new Set());

  const registerSignOutTeardown = useCallback(
    (teardown: () => Promise<void> | void): (() => void) => {
      signOutTeardownsRef.current.add(teardown);
      return () => {
        signOutTeardownsRef.current.delete(teardown);
      };
    },
    [],
  );

  useEffect(() => {
    activeSpaceIdRef.current = activeSpaceId;
  }, [activeSpaceId]);

  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);

  useEffect(() => {
    userIdRef.current = userId;
    setSkippedRecovery(readRecoverySkip(userId));
  }, [userId]);

  useEffect(() => {
    const isSynced = state === "synced";
    const isSyncing = state === "syncing";
    syncedRef.current = isSynced;
    syncingRef.current = isSyncing;
    // Open on synced once any snapshot arrived (a zero-room user still sees
    // the empty shell); during syncing, only when cached rooms are present.
    if (isSynced) {
      if (roomsReceivedRef.current) setRoomsReady(true);
    } else if (isSyncing && currentRoomCountRef.current > 0) {
      setRoomsReady(true);
    }
  }, [state]);

  useEffect(() => {
    roomsReceivedRef.current = false;
    syncedRef.current = false;
    currentRoomCountRef.current = 0;
    setRoomsReady(false);
  }, [client]);

  const currentRecoveryUserId = useCallback(
    () => clientRef.current?.bindings.getUserId() ?? userIdRef.current,
    [],
  );

  useEffect(() => {
    let cancelled = false;
    if (!client || !userId || !needsRecovery) {
      setRecoveryDevices([]);
      return;
    }
    void matrixGetOwnDevices(client)
      .then((devices) => {
        if (cancelled) return;
        const currentDeviceId = matrixGetDeviceId(client);
        setRecoveryDevices(
          devices
            .map((device) => ({ ...device, label: deviceLabel(device) }))
            .filter((device) => canUseDeviceForRecovery(device, currentDeviceId)),
        );
      })
      .catch((err) => {
        console.warn("load recovery devices failed", err);
        if (!cancelled) setRecoveryDevices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [client, userId, needsRecovery]);

  const clearSkippedRecovery = useCallback(() => {
    clearRecoverySkip(currentRecoveryUserId());
    setSkippedRecovery(false);
  }, [currentRecoveryUserId]);

  const persistActiveRoomId = useCallback(
    (id: string): void => {
      const currentActiveSpaceId = activeSpaceIdRef.current;
      if (!currentActiveSpaceId) return;
      if (!isValidRoomForSpace(roomsRef.current, currentActiveSpaceId, id)) return;
      lastRoomsBySpaceRef.current = writeLastRoomForSpace(
        navigationStorageScope,
        userIdRef.current,
        lastRoomsBySpaceRef.current,
        currentActiveSpaceId,
        id,
      );
    },
    [navigationStorageScope],
  );

  const applyActiveRoomId = useCallback((id: string | null, origin: ActiveRoomOrigin) => {
    if (activeRoomIdRef.current !== id) setActiveThreadRoot(null);
    activeRoomIdRef.current = id;
    activeRoomOriginRef.current = id ? origin : null;
    setActiveRoomIdState(id);
  }, []);

  useEffect(() => {
    lastRoomsBySpaceRef.current = readLastRoomsBySpace(navigationStorageScope, userId);
    setLastRoomsVersion((value) => value + 1);
  }, [navigationStorageScope, userId]);

  useEffect(() => {
    if (!client || !userId) return;
    const stop = startScheduledDispatcher(client, userId);
    scheduledDispatcherStopRef.current = stop;
    return () => {
      if (scheduledDispatcherStopRef.current === stop) scheduledDispatcherStopRef.current = null;
      stop();
    };
  }, [client, userId]);

  useEffect(() => {
    if (state !== "synced" && !(state === "syncing" && roomsReady)) return;
    const next = repairSpaceSelection(
      rooms,
      activeOrgSpaceId,
      activeSpaceId && activeSpaceId !== activeOrgSpaceId ? activeSpaceId : null,
    );

    const orgChanged = next.orgSpaceId !== activeOrgSpaceId;
    const spaceChanged = next.activeSpaceId !== activeSpaceId;

    if (orgChanged) setActiveOrgSpaceIdState(next.orgSpaceId);
    if (spaceChanged) setActiveSpaceIdState(next.activeSpaceId);
    if (
      orgChanged ||
      spaceChanged ||
      navigationSelectionNeedsWrite(navigationStorageScope, next.orgSpaceId, next.childSpaceId)
    ) {
      writeNavigationSelection(navigationStorageScope, next.orgSpaceId, next.childSpaceId);
    }

    let currentActiveRoomId = activeRoomIdRef.current;
    if (
      currentActiveRoomId &&
      !isRoomVisibleInSpaceSelection(rooms, next.activeSpaceId, currentActiveRoomId)
    ) {
      activeRoomIdRef.current = null;
      activeRoomOriginRef.current = null;
      setActiveRoomIdState(null);
      setPendingJump(null);
      setActiveThreadRoot(null);
      currentActiveRoomId = null;
    }

    const persistedRoomId = next.activeSpaceId
      ? lastRoomsBySpaceRef.current[next.activeSpaceId]
      : null;
    if (
      currentActiveRoomId &&
      activeRoomOriginRef.current === "auto" &&
      persistedRoomId &&
      persistedRoomId !== currentActiveRoomId &&
      isValidRoomForSpace(rooms, next.activeSpaceId, persistedRoomId)
    ) {
      applyActiveRoomId(persistedRoomId, "auto");
      return;
    }

    if (autoSelectRoom && !currentActiveRoomId && next.activeSpaceId) {
      const nextRoomId = pickRoomForSpace(rooms, next.activeSpaceId, lastRoomsBySpaceRef.current);
      if (nextRoomId) applyActiveRoomId(nextRoomId, "auto");
    }
  }, [
    state,
    rooms,
    roomsReady,
    activeOrgSpaceId,
    activeSpaceId,
    autoSelectRoom,
    navigationStorageScope,
    lastRoomsVersion,
    applyActiveRoomId,
  ]);

  useEffect(() => {
    if (!client) return;
    const unsub = matrixSubscribeUnreadTimeline(client, (groups) => {
      setUnreadGroups(groups);
    });
    return () => {
      unsub();
    };
  }, [client]);

  useEffect(() => {
    if (!client) return;
    const unsub = matrixSubscribeMentionsFeed(client, (items) => {
      setMentionsFeed(
        items.filter((item) => {
          if (clearedMentionIdsRef.current.has(item.eventId)) return false;
          const watermark = clearedMentionWatermarksRef.current.get(item.roomId);
          return watermark === undefined || item.ts > watermark;
        }),
      );
    });
    return () => {
      unsub();
    };
  }, [client]);

  const rejectPendingInteractiveAuth = useCallback((err: Error, clearRequest = true) => {
    const reject = pendingUiaRejectRef.current;
    pendingUiaRejectRef.current = null;
    if (clearRequest) setPendingUiaRequest(null);
    reject?.(err);
  }, []);

  // Open a new pipeline generation and return the matching
  // cancellation predicate. The name is deliberate — calling this
  // BUMPS the global generation counter, so a previously-captured
  // predicate immediately returns true at its next check.
  // Combined with the mount flag and an optional caller-local flag,
  // this gives every entry point the same three-way race protection:
  // unmount, supersede, effect-local abort.
  const beginPipelineGeneration = useCallback(
    (extra?: () => boolean): (() => boolean) => {
      rejectPendingInteractiveAuth(new Error("Interactive auth request superseded"));
      pipelineGenRef.current += 1;
      const myGen = pipelineGenRef.current;
      return () => {
        if (!mountedRef.current) return true;
        if (pipelineGenRef.current !== myGen) return true;
        return extra ? extra() : false;
      };
    },
    [rejectPendingInteractiveAuth],
  );

  const requestInteractiveAuth = useCallback(
    (req: IAuthData): Promise<IAuthData> => {
      const client = clientRef.current;
      const currentUserId = client ? matrixGetUserId(client) : null;
      const stages = uiaStages(req);
      rejectPendingInteractiveAuth(new Error("Interactive auth request superseded"));

      if (!client || !currentUserId) {
        const err = new Error("Interactive auth unavailable");
        recordSdkBreadcrumb("matrix.uia", "UIA requested before user id was available", err, {
          stages: stages.join(","),
          hasSession: Boolean(req.session),
        });
        return Promise.reject(err);
      }

      const approvalUrl = oauthApprovalUrl(req);
      if (approvalUrl) {
        return new Promise<IAuthData>((resolve, reject) => {
          pendingUiaRejectRef.current = reject;
          setPendingUiaRequest({
            flow: { kind: "oauth", url: approvalUrl },
            onOpen: () => {
              void openExternal(approvalUrl).catch((err: unknown) => {
                recordSdkBreadcrumb("matrix.uia", "OAuth approval page failed to open", err, {
                  stages: stages.join(","),
                });
              });
            },
            onSubmit: () => {
              return client.bindings
                .completeCrossSigningOauth()
                .then(() => {
                  pendingUiaRejectRef.current = null;
                  setPendingUiaRequest(null);
                  resolve({ auth: null });
                })
                .catch((err: unknown) => {
                  recordSdkBreadcrumb("matrix.uia", "OAuth approval did not complete", err, {
                    stages: stages.join(","),
                  });
                  reject(err);
                });
            },
            onCancel: () => {
              const err = new Error("Interactive auth cancelled");
              pendingUiaRejectRef.current = null;
              setPendingUiaRequest(null);
              void client.bindings.cancelCrossSigningOauth().catch((cancelErr: unknown) => {
                recordSdkBreadcrumb("matrix.uia", "OAuth approval cancellation failed", cancelErr, {
                  stages: stages.join(","),
                });
              });
              reject(err);
            },
          });
        });
      }

      if (!hasSinglePasswordFlow(req)) {
        const err = new Error("Unsupported interactive-auth flow");
        recordSdkBreadcrumb("matrix.uia", "unknown UIA flow", err, {
          stages: stages.join(","),
          hasSession: Boolean(req.session),
        });
        if (!unknownUiaCapturedRef.current) {
          unknownUiaCapturedRef.current = true;
          captureSdkException("matrix.uia", err);
        }
        return new Promise<IAuthData>((_resolve, reject) => {
          pendingUiaRejectRef.current = reject;
          setPendingUiaRequest({
            flow: { kind: "unknown", stages },
            onSubmit: () => undefined,
            onCancel: () => {
              pendingUiaRejectRef.current = null;
              setPendingUiaRequest(null);
              reject(err);
            },
          });
        });
      }

      return new Promise<IAuthData>((resolve, reject) => {
        pendingUiaRejectRef.current = reject;
        setPendingUiaRequest({
          flow: { kind: "password", userId: currentUserId },
          onSubmit: (password) => {
            pendingUiaRejectRef.current = null;
            setPendingUiaRequest(null);
            resolve({
              auth: {
                type: "m.login.password",
                session: req.session,
                identifier: { type: "m.id.user", user: currentUserId },
                password,
              },
            });
          },
          onCancel: () => {
            const err = new Error("Interactive auth cancelled");
            pendingUiaRejectRef.current = null;
            setPendingUiaRequest(null);
            reject(err);
          },
        });
      });
    },
    [rejectPendingInteractiveAuth],
  );

  // Build the option bag that every pipeline call needs. Captures
  // the React state setters and refs as plain callbacks so the
  // pipeline stays framework-agnostic. `isCancelled` is built per
  // call (each entry point gets its own generation) so two
  // concurrent invocations don't accidentally share a predicate.
  const handlePipelineRooms = useCallback((list: RoomSummary[]) => {
    roomsReceivedRef.current = true;
    currentRoomCountRef.current = list.length;
    setRooms(list);
    if (syncedRef.current) setRoomsReady(true);
    if (syncingRef.current && list.length > 0) setRoomsReady(true);
  }, []);

  const buildPipelineOptions = useCallback(
    (isCancelled: () => boolean, onState: (state: ClientState) => void) => ({
      isCancelled,
      onState,
      setRooms: handlePipelineRooms,
      setSavedItems,
      setDisplayName,
      setUserId,
      interactiveAuth: requestInteractiveAuth,
      setCryptoReady,
      setCryptoError: (stage: CryptoBootstrapStage, err: unknown) => {
        setCryptoErrorState(toCryptoBootstrapError(stage, err));
      },
      skipRecoveryGate: () => readRecoverySkip(currentRecoveryUserId()),
      setNeedsRecovery: (value: boolean) => {
        if (value) setSkippedRecovery(readRecoverySkip(currentRecoveryUserId()));
        setNeedsRecovery(value);
      },
      setPendingRecoveryKey,
      syncActiveRef,
      roomsUnsubRef,
      savedUnsubRef,
    }),
    [currentRecoveryUserId, requestInteractiveAuth, handlePipelineRooms],
  );

  const retryCryptoBootstrap = useCallback(
    async (options?: { skipRecoveryGate?: boolean }): Promise<void> => {
      if (retryCryptoRef.current) return;
      const client = clientRef.current;
      if (!client) return;
      retryCryptoRef.current = true;
      const retryGen = pipelineGenRef.current;
      try {
        setCryptoErrorState(null);
        setNeedsRecovery(false);
        setPendingRecoveryKey(null);
        setCryptoReady(false);
        const isCancelled = () =>
          !mountedRef.current ||
          clientRef.current !== client ||
          pipelineGenRef.current !== retryGen;
        await bootstrapCrypto(
          client,
          buildPipelineOptions(isCancelled, (next) => {
            if (!isCancelled()) setState(next);
          }),
          isCancelled,
          { interactiveAuth: requestInteractiveAuth, skipRecoveryGate: options?.skipRecoveryGate },
        );
      } finally {
        retryCryptoRef.current = false;
      }
    },
    [buildPipelineOptions, requestInteractiveAuth],
  );

  // Drive the post-login pipeline (subscriptions + identity before
  // crypto → sync) for every path that yields an authenticated client.
  // The implementation lives in `~/lib/post-login-pipeline` so SSO and
  // restore share one code path.
  const startSessionPipeline = useCallback(
    async (
      bootedClient: Client,
      isCancelled: () => boolean,
      onState: (state: ClientState) => void,
    ): Promise<void> => {
      await runPostLoginPipeline(bootedClient, buildPipelineOptions(isCancelled, onState));
    },
    [buildPipelineOptions],
  );

  const recoverStaleCryptoStore = useCallback(
    async (staleClient: Client | null, isCancelled: () => boolean) => {
      if (isCancelled()) return;
      try {
        teardownSub(roomsUnsubRef);
        teardownSub(savedUnsubRef);
        syncActiveRef.current = false;
        const storeName = storeNameFromSession(
          await readSecret("matrix.session").catch(() => null),
        );
        if (staleClient) {
          clearAvatarMedia(staleClient);
          try {
            await logout(staleClient);
          } catch (cleanupErr) {
            console.warn("Stale crypto store logout failed", cleanupErr);
          }
        }
        await deleteChatIndexedDbDatabases(storeName ?? MATRIX_STORE_PREFIX);
        if (isCancelled()) return;
        const freshClient = await createClient(homeserverUrl);
        if (isCancelled()) return;
        clientRef.current = freshClient;
        setClient(freshClient);
        setDisplayName(null);
        setUserId(null);
        unknownUiaCapturedRef.current = false;
        clearSkippedRecovery();
        setRooms([]);
        roomsReceivedRef.current = false;
        currentRoomCountRef.current = 0;
        setRoomsReady(false);
        activeRoomIdRef.current = null;
        activeRoomOriginRef.current = null;
        setActiveRoomIdState(null);
        setSavedItems([]);
        setMentionsFeed([]);
        clearedMentionIdsRef.current.clear();
        clearedMentionWatermarksRef.current.clear();
        setActiveViewState("room");
        setPendingJump(null);
        setActiveThreadRoot(null);
        setCryptoReady(false);
        setCryptoErrorState(null);
        setNeedsRecovery(false);
        setPendingRecoveryKey(null);
        setLastError(null);
        setState("login");
      } catch (recoveryErr) {
        console.error("Stale crypto store recovery failed", recoveryErr);
        if (!isCancelled()) {
          setLastError(recoveryErr);
          setState("error");
        }
      }
    },
    [clearSkippedRecovery, homeserverUrl],
  );

  const signOutLocalTeardown = useCallback(
    async (broadcast = true) => {
      const client = clientRef.current;
      if (!client) return;
      clearAvatarMedia(client);
      pipelineGenRef.current += 1;
      syncActiveRef.current = false;
      pendingCallbackRef.current = null;
      pendingDirectRoomsRef.current.clear();
      scheduledDispatcherStopRef.current?.();
      scheduledDispatcherStopRef.current = null;
      teardownSub(roomsUnsubRef);
      teardownSub(savedUnsubRef);
      for (const teardown of signOutTeardownsRef.current) {
        try {
          await teardown();
        } catch (teardownErr) {
          console.warn("Sign-out teardown failed", teardownErr);
        }
      }
      try {
        await logout(client, { broadcast });
      } catch (cleanupErr) {
        // Best-effort: a throwing local cleanup must not strand the
        // user in a half-torn-down session — finish the reset and
        // route back to login regardless.
        console.warn("Sign-out cleanup failed", cleanupErr);
      }
      // Keep `clientRef` / `client` populated after sign-out — the same
      // handle survives the next sign-in, and clearing it would strand
      // any hook that captured a reference mid-render. We null out the
      // user-identity fields instead, which is what consumers actually
      // gate on.
      setDisplayName(null);
      clearSkippedRecovery();
      setUserId(null);
      unknownUiaCapturedRef.current = false;
      setRooms([]);
      roomsReceivedRef.current = false;
      currentRoomCountRef.current = 0;
      setRoomsReady(false);
      activeRoomIdRef.current = null;
      activeRoomOriginRef.current = null;
      setActiveRoomIdState(null);
      setSavedItems([]);
      setMentionsFeed([]);
      clearedMentionIdsRef.current.clear();
      clearedMentionWatermarksRef.current.clear();
      setActiveViewState("room");
      setPendingJump(null);
      setActiveThreadRoot(null);
      setCryptoReady(false);
      setCryptoErrorState(null);
      setNeedsRecovery(false);
      setPendingRecoveryKey(null);
      rejectPendingInteractiveAuth(new Error("Interactive auth cancelled"));
      setState("login");
    },
    [clearSkippedRecovery, rejectPendingInteractiveAuth],
  );

  // OIDC completion: delegated to `runOidcLogin` which exchanges the
  // MAS callback query for an access token and drives the shared
  // post-login pipeline. Any change to the OIDC entry sequence belongs
  // there, not here — duplicating it locally is the structural mistake
  // #1311 / #2377 set out to remove. The cancellation predicate uses
  // the same generation counter as the boot effect so a re-login race
  // against an in-flight restore-path pipeline supersedes (rather than
  // double-writes to) the shared state setters.
  const completeLogin = useCallback(
    async (callbackQuery: string) => {
      const client = clientRef.current;
      if (!client) {
        pendingCallbackRef.current = callbackQuery;
        return;
      }
      setState("loading");
      const isCancelled = beginPipelineGeneration();
      const onState = (next: ClientState, err?: unknown) => {
        if (isCancelled()) return;
        if (next === "syncing") {
          resetRoomsGateOnSyncing();
        }
        if (next === "login" && (isUnknownTokenError(err) || isInvalidGrantError(err))) {
          if (preAuthedClient) {
            setLastError(err);
            setState("error");
          } else {
            void signOutLocalTeardown(false);
          }
          return;
        }
        setLastError(next === "error" ? (err ?? null) : null);
        setState(next);
      };
      const onError = (_err: unknown) => {
        if (isCancelled()) return;
        setLastError(_err);
        setState("error");
      };
      await runOidcLogin(client, callbackQuery, {
        ...buildPipelineOptions(isCancelled, onState),
        onError: async (err) => {
          if (isCancelled()) return;
          if (isStaleCryptoStoreError(err)) {
            console.warn("Stale crypto store detected after login, clearing local session", err);
            await recoverStaleCryptoStore(clientRef.current, isCancelled);
            return;
          }
          onError(err);
        },
      });
    },
    [
      buildPipelineOptions,
      beginPipelineGeneration,
      recoverStaleCryptoStore,
      signOutLocalTeardown,
      preAuthedClient,
    ],
  );

  const deliverOidcCallback = useCallback(
    (query: string) => {
      if (handledOidcQueriesRef.current.has(query)) return;
      handledOidcQueriesRef.current.add(query);
      void completeLogin(query);
    },
    [completeLogin],
  );

  // Boot: create the client, try to restore a previous session, and start
  // sync. If there's no session we land on "login". A token that arrived
  // before boot finished gets replayed here. When `preAuthedClient` is
  // provided (embed surface), the SSO restore / login branches are
  // skipped — the host has already exchanged credentials and the
  // provider only needs to run the post-login pipeline.
  useEffect(() => {
    let cancelled = false;
    // The boot effect's predicate stacks its own `cancelled` flag on
    // top of the shared mount/generation check. Effect-local
    // cancellation (a re-mount during StrictMode) bails without
    // bumping the global generation; conversely, a deliberate
    // re-login (`completeLogin` ↔ generation bump) supersedes any
    // restore-path pipeline this effect kicked off.
    const isCancelled = beginPipelineGeneration(() => cancelled);
    const onState = (next: ClientState, err?: unknown) => {
      if (isCancelled()) return;
      if (next === "syncing") {
        resetRoomsGateOnSyncing();
      }
      if (next === "login" && (isUnknownTokenError(err) || isInvalidGrantError(err))) {
        if (preAuthedClient) {
          setLastError(err);
          setState("error");
        } else {
          void signOutLocalTeardown(false);
        }
        return;
      }
      setLastError(next === "error" ? (err ?? null) : null);
      setState(next);
    };

    async function boot() {
      try {
        if (preAuthedClient) {
          clientRef.current = preAuthedClient;
          setClient(preAuthedClient);
          await startSessionPipeline(preAuthedClient, isCancelled, onState);
          return;
        }

        // Clear any previously-booted client *before* yielding to
        // `createClient` so that any concurrent input that fires
        // during the await window (a queued OIDC callback, a `signIn()`
        // click) doesn't run against a stale client pointing at the
        // old homeserver. With `clientRef` cleared, `completeLogin`
        // hits its `pendingCallbackRef` queue branch and replays the
        // callback after the new client lands. Hot homeserver-URL swaps
        // aren't a normal flow (the standalone shell never changes it
        // post-mount; the embed only reaches this branch when
        // `preAuthedClient` is absent — which it always is on the
        // standalone path), but the cleanup runs on unmount /
        // StrictMode re-invoke / boot-effect re-fire so being
        // explicit here is cheaper than reasoning about all of those
        // paths.
        clientRef.current = null;
        setClient(null);
        const bootedClient = await createClient(homeserverUrl);
        if (isCancelled()) return;
        clientRef.current = bootedClient;
        setClient(bootedClient);
        markBoot("client-created");

        const queued = pendingCallbackRef.current;
        if (queued) {
          // A replayed callback lands before boot knows whether a persisted
          // session restores; if one does restore, the callback is stale, so
          // run the session pipeline instead of driving completeLogin onto
          // the error screen and discarding the live session.
          const restored = await tryRestoreSession(bootedClient);
          if (isCancelled()) return;
          markBoot("session-restored");
          pendingCallbackRef.current = null;
          if (restored) {
            await startSessionPipeline(bootedClient, isCancelled, onState);
            return;
          }
          await completeLogin(queued);
          return;
        }

        if (platform === "tauri") {
          const legacy = await detectLegacySnapshot();
          if (isCancelled()) return;
          if (legacy) {
            await wipeDesktopSecrets();
            if (isCancelled()) return;
            setForceReloginBanner(true);
            setState("login");
            return;
          }
        }

        const restored = await tryRestoreSession(bootedClient);
        if (isCancelled()) return;
        markBoot("session-restored");

        if (!restored) {
          setState("login");
          return;
        }
        await startSessionPipeline(bootedClient, isCancelled, onState);
      } catch (err) {
        // Stale persisted session: MAS / the homeserver no longer
        // recognises the access token (revoked, expired, or issued
        // against a since-replaced MAS database). The session blob in
        // IndexedDB cannot be salvaged; wipe it and fall back to the
        // login screen so the user signs in fresh. Without this branch
        // the boot effect would surface "Something went wrong / Check
        // the homeserver URL and reload" and trap the user — the
        // homeserver is fine, only the local session is dead.
        if (!preAuthedClient && isStaleCryptoStoreError(err)) {
          console.warn("Stale crypto store detected during boot, clearing local session", err);
          await recoverStaleCryptoStore(clientRef.current, isCancelled);
          return;
        }
        if (preAuthedClient && (isUnknownTokenError(err) || isInvalidGrantError(err))) {
          if (!isCancelled()) {
            setLastError(err);
            setState("error");
          }
          return;
        }
        if (isUnknownTokenError(err) || isInvalidGrantError(err)) {
          if (!isCancelled()) await signOutLocalTeardown(false);
          return;
        }
        if (!preAuthedClient && isKeychainUnavailableError(err)) {
          recordSdkBreadcrumb("chat.keychain.unavailable", "Keychain unavailable", err.kind, {
            kind: err.kind,
          });
          if (!isCancelled()) setKeychainError(err);
          return;
        }
        if (!preAuthedClient && isKeychainTransientError(err)) {
          recordSdkBreadcrumb(
            "chat.keychain.transient",
            "Keychain transiently unavailable",
            err.kind,
            { kind: err.kind },
          );
          if (!isCancelled()) {
            setLastError(err);
            setState("error");
          }
          return;
        }
        console.error("Matrix boot failed", err);
        if (!isCancelled()) {
          setLastError(err);
          setState("error");
        }
      }
    }

    void boot();

    return () => {
      cancelled = true;
      syncActiveRef.current = false;
      teardownSub(roomsUnsubRef);
      teardownSub(savedUnsubRef);
      const client = clientRef.current;
      if (client) {
        clearAvatarMedia(client);
        void stopSync(client);
      }
    };
    // `homeserverUrl` is only consumed inside the `!preAuthedClient`
    // branch of `boot()` — the embed surface (`preAuthedClient !==
    // null`) returns before the `createClient(homeserverUrl)` call
    // and never reads the value. Keeping it in the dep array is
    // correct for the standalone shell (a hot homeserver swap there
    // must re-boot the client) and benign for the embed: the host's
    // `homeserverUrl` prop on `EmbeddableChatProvider` is memoised
    // through `runtimeOverrides` and stays stable across renders, so
    // the embed never observes an identity flip. Future readers:
    // don't strip the dep without also accounting for the standalone
    // path — the duplicated boot-vs-pipeline split this file pays for
    // came from #2377 and the lesson is still load-bearing.
  }, [
    completeLogin,
    beginPipelineGeneration,
    preAuthedClient,
    startSessionPipeline,
    recoverStaleCryptoStore,
    signOutLocalTeardown,
    homeserverUrl,
    bootRetryToken,
  ]);

  // Mount lifecycle: the cleanup flips `mountedRef` so any in-flight
  // pipeline (boot effect or `completeLogin`) bails before its
  // post-await setState writes into a torn-down React tree. Kept in
  // its own effect so the boot effect can re-run (HMR, StrictMode
  // double-invoke, prop change) without prematurely flipping the
  // mount flag — only a true unmount runs this cleanup.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      rejectPendingInteractiveAuth(new Error("Interactive auth cancelled"), false);
    };
  }, [rejectPendingInteractiveAuth]);

  // Tauri deeplink: subscribe to the `oidc-callback` event the Rust
  // side emits when `com.nurion.chat:/oidc/callback?code=…&state=…`
  // arrives. The payload carries the raw query string the SDK's
  // `finishOidcLogin` parses; we forward it to `completeLogin`
  // unchanged.
  //
  // The listener registers asynchronously, so the component may unmount
  // before `listen()` resolves. The cancellation flag ensures we unsub
  // as soon as the handle lands.
  useEffect(() => {
    if (platform !== "tauri") return;
    let cancelled = false;
    let unlisten: (() => void) | null = null;

    void (async () => {
      const { listen } = await import("@tauri-apps/api/event");
      const off = await listen<{ callbackQuery: string }>("oidc-callback", (event) => {
        const query = event.payload?.callbackQuery;
        if (!query) return;
        deliverOidcCallback(query);
        // The host also buffers this query for cold-start delivery. We
        // handled it live, so discard the buffered copy — otherwise a
        // re-login while running would leave a stale code that the next
        // launch's drain would replay against an already-spent grant.
        void takeLaunchOidcCallback();
      });
      if (cancelled) {
        off();
        return;
      }
      unlisten = off;
      // Drain a cold-start callback the host buffered when the OS launched
      // the app via the callback deep-link before this listener attached.
      const buffered = await takeLaunchOidcCallback();
      if (!cancelled && buffered) deliverOidcCallback(buffered);
    })();

    return () => {
      cancelled = true;
      if (unlisten) unlisten();
    };
  }, [deliverOidcCallback]);

  // Tauri builds the store, fetches MAS metadata, and registers the client
  // before the authorize URL exists; doing that while the login screen is
  // showing turns the Sign in tap into an instant browser open.
  const preparedLoginRef = useRef<PreparedLogin | null>(null);
  useEffect(() => {
    if (platform !== "tauri" || state !== "login" || !client) return;
    const pending = matrixStartOidcLogin(client, getCallbackUrl());
    pending.catch(() => undefined);
    preparedLoginRef.current = { client, preparedAt: Date.now(), pending };
  }, [client, state]);

  const signIn = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return;

    const prepared = preparedLoginRef.current;
    preparedLoginRef.current = null;
    const usable =
      prepared !== null &&
      prepared.client === client &&
      Date.now() - prepared.preparedAt < PREPARED_LOGIN_TTL_MS;

    try {
      const { url } = usable
        ? await prepared.pending.catch(() => matrixStartOidcLogin(client, getCallbackUrl()))
        : await matrixStartOidcLogin(client, getCallbackUrl());
      if (platform === "tauri") {
        // MAS login URLs are derived from configured homeserver metadata, not Matrix room state.
        if (!(await authenticateInApp(url))) await openExternal(url);
        setForceReloginBanner(false);
      } else {
        // Same-tab redirect: the SDK's in-flight PKCE state was
        // persisted to IndexedDB by `startOidcLogin`. On the callback
        // page load, `WebCallbackHandler` restores it and completes
        // the token exchange.
        window.location.assign(url);
        setForceReloginBanner(false);
      }
    } catch (err) {
      // Re-throw: the mobile CTA only resets its "opening browser" state on rejection.
      setLastError(err);
      throw err;
    }
  }, []);

  const retryKeychain = useCallback(async (): Promise<void> => {
    try {
      await refreshStrongholdSnapshotKey();
      setKeychainError(null);
      setBootRetryToken((value) => value + 1);
    } catch (err) {
      // A macOS process denied keychain-item access keeps failing for its lifetime, so relaunch — the proven recovery — instead of re-parking on a dead-end panel.
      if (platform === "tauri") {
        recordSdkBreadcrumb(
          "chat.keychain.retry-relaunch",
          "Keychain retry failed in-process; relaunching",
          err,
        );
        await relaunchApp();
        return;
      }
      setKeychainError(err);
    }
  }, []);

  useEffect(() => {
    return subscribeSessionBroadcast(() => {
      void signOutLocalTeardown(false).catch((err) => {
        console.warn("Remote Matrix sign-out teardown failed", err);
      });
    });
  }, [signOutLocalTeardown]);

  const signOut = useCallback(async () => {
    await signOutLocalTeardown(true);
  }, [signOutLocalTeardown]);

  const recoverToLogin = useCallback(async (): Promise<void> => {
    if (!clientRef.current) {
      setState("login");
      return;
    }
    await signOutLocalTeardown(true);
  }, [signOutLocalTeardown]);

  const createRoom = useCallback(
    async (opts: CreateRoomOptions & { parentSpaceId?: string | null }): Promise<string> => {
      const client = clientRef.current;
      if (!client) {
        throw new Error("createRoom called before Matrix client is ready");
      }
      if (!activeOrgSpaceId) {
        throw new Error("Select a space before creating a room");
      }
      const { parentSpaceId, ...roomOpts } = opts;
      const parentSpace =
        parentSpaceId === undefined ? (activeSpaceId ?? activeOrgSpaceId) : parentSpaceId;
      const roomId = await matrixCreateRoom(client, {
        ...roomOpts,
        visibleToSpaceId: parentSpace ?? activeOrgSpaceId,
      });
      if (parentSpace) {
        const serverName = roomId.split(":")[1] ?? "nurion";
        await matrixAddSpaceChild(client, parentSpace, roomId, [serverName]).catch((err) => {
          console.warn("addSpaceChild failed after room creation", err);
        });
      }
      return roomId;
    },
    [activeOrgSpaceId, activeSpaceId],
  );

  const moveRoomToSpace = useCallback(
    async ({ roomId, spaceId }: { roomId: string; spaceId: string | null }): Promise<void> => {
      const client = clientRef.current;
      if (!client) {
        throw new Error("moveRoomToSpace called before Matrix client is ready");
      }
      await matrixMoveRoomToSpace(client, roomsRef.current, roomId, spaceId);
    },
    [],
  );

  const createSpace = useCallback(
    async (opts: {
      name: string;
      topic?: string;
      parentSpaceId?: string;
      isPrivate?: boolean;
    }): Promise<string> => {
      const client = clientRef.current;
      if (!client) {
        throw new Error("createSpace called before Matrix client is ready");
      }
      if (!activeOrgSpaceId) {
        throw new Error("Select a space before creating a sub-space");
      }
      const parentSpace = opts.parentSpaceId ?? activeOrgSpaceId;
      const spaceId = await matrixCreateRoom(client, {
        name: opts.name,
        topic: opts.topic,
        isPrivate: opts.isPrivate ?? true,
        roomType: "m.space",
        visibleToSpaceId: parentSpace,
      });
      const serverName = spaceId.split(":")[1] ?? "nurion";
      await matrixAddSpaceChild(client, parentSpace, spaceId, [serverName]);
      return spaceId;
    },
    [activeOrgSpaceId],
  );

  const findDirectRoom = useCallback((userId: string): RoomSummary | null => {
    return matrixFindDirectRoom(
      roomsRef.current.filter((room) => room.archived === null),
      userId,
    );
  }, []);

  const addDirectRoom = useCallback(async (userId: string, roomId: string): Promise<void> => {
    const client = clientRef.current;
    if (!client) {
      throw new Error("addDirectRoom called before Matrix client is ready");
    }
    const write = directWriteRef.current
      .catch(() => undefined)
      .then(() => matrixAddDirectRoom(client, userId, roomId));
    directWriteRef.current = write;
    await write;
  }, []);

  const addDirectRooms = useCallback(async (userIds: string[], roomId: string): Promise<void> => {
    const client = clientRef.current;
    if (!client) {
      throw new Error("addDirectRooms called before Matrix client is ready");
    }
    const write = directWriteRef.current
      .catch(() => undefined)
      .then(() => matrixAddDirectRooms(client, userIds, roomId));
    directWriteRef.current = write;
    await write;
  }, []);

  const createDirectRoom = useCallback(
    async ({ userIds }: { userIds: string[] }): Promise<string> => {
      const peers = [...new Set(userIds)].sort();
      // Never parent a DM into a space (m.space.child would leak a private DM id into shared state).
      const existing = roomsRef.current.find(
        (room) =>
          room.isDirect &&
          room.archived === null &&
          room.directUserIds.length === peers.length &&
          [...room.directUserIds].sort().every((userId, index) => userId === peers[index]),
      );
      if (existing) return existing.id;

      const key = peers.join("\u0000");
      let pending = pendingDirectRoomsRef.current.get(key);
      if (pending?.complete && pending.roomId) {
        const roomId = pending.roomId;
        const current = roomsRef.current.find((room) => room.id === roomId);
        if (!current) return roomId;
        if (current.archived === null) return roomId;
        pendingDirectRoomsRef.current.delete(key);
        pending = undefined;
      }
      if (pending?.inFlight) return pending.inFlight;

      const entry = pending ?? { roomId: null, complete: false, inFlight: null };
      const operation = (async () => {
        if (!entry.roomId) {
          entry.roomId = await createRoom({
            isDirect: true,
            isPrivate: true,
            invite: peers,
            parentSpaceId: null,
          });
        }
        await addDirectRooms(peers, entry.roomId);
        entry.complete = true;
        return entry.roomId;
      })();
      entry.inFlight = operation;
      pendingDirectRoomsRef.current.set(key, entry);
      try {
        return await operation;
      } finally {
        entry.inFlight = null;
        if (!entry.roomId) pendingDirectRoomsRef.current.delete(key);
      }
    },
    [addDirectRooms, createRoom],
  );

  const searchUserDirectory = useCallback(async (term: string): Promise<UserDirectoryEntry[]> => {
    const client = clientRef.current;
    if (!client) return [];
    return matrixSearchUserDirectory(client, term);
  }, []);

  const searchRooms = useCallback(async (term: string): Promise<RoomSearchResult[]> => {
    const client = clientRef.current;
    if (!client) return [];
    return matrixSearchRooms(client, term);
  }, []);

  const setActiveOrgSpaceId = useCallback(
    (id: string | null) => {
      setActiveOrgSpaceIdState(id);
      setActiveSpaceIdState(id);
      writeNavigationSelection(navigationStorageScope, id, null);
      if (isDmRoom(roomsRef.current, activeRoomIdRef.current)) return;
      applyActiveRoomId(
        pickRoomForSpace(roomsRef.current, id, lastRoomsBySpaceRef.current),
        "auto",
      );
    },
    [applyActiveRoomId, navigationStorageScope],
  );

  const setActiveSpaceId = useCallback(
    (id: string | null) => {
      setActiveSpaceIdState(id);
      writeNavigationSelection(
        navigationStorageScope,
        activeOrgSpaceId,
        id && id !== activeOrgSpaceId ? id : null,
      );
      if (isDmRoom(roomsRef.current, activeRoomIdRef.current)) return;
      applyActiveRoomId(
        pickRoomForSpace(roomsRef.current, id, lastRoomsBySpaceRef.current),
        "auto",
      );
    },
    [activeOrgSpaceId, applyActiveRoomId, navigationStorageScope],
  );

  const setActiveRoomId = useCallback(
    (id: string | null) => {
      // Single-slot thread state: switching to a different room (or
      // clearing the active room entirely) closes the open thread.
      // The brief is explicit — switching rooms closes the thread,
      // not "the thread follows you". Re-selecting the same room is
      // a no-op so a sidebar re-click doesn't pop open threads.
      if (activeRoomIdRef.current !== id) setActiveThreadRoot(null);
      activeRoomIdRef.current = id;
      activeRoomOriginRef.current = id ? "user" : null;
      setActiveRoomIdState(id);
      if (id) persistActiveRoomId(id);
      // Switching rooms means the user wants the timeline — leaving them
      // on Saved after a room click would feel like a broken toggle.
      setActiveViewState("room");
    },
    [persistActiveRoomId],
  );

  const setActiveView = useCallback((view: ActiveView) => {
    setActiveViewState(view);
  }, []);

  const saveEvent = useCallback(
    async ({ roomId, eventId }: { roomId: string; eventId: string }) => {
      const client = clientRef.current;
      if (!client) throw new Error("saveEvent called before Matrix client is ready");
      return matrixSaveEvent(client, { roomId, eventId });
    },
    [],
  );

  const unsaveEvent = useCallback(async (eventId: string) => {
    const client = clientRef.current;
    if (!client) throw new Error("unsaveEvent called before Matrix client is ready");
    await matrixUnsaveEvent(client, eventId);
  }, []);

  const clearSaved = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return;
    await matrixClearSaved(client);
  }, []);

  const clearMentions = useCallback(
    async (items?: MentionFeedItem[]) => {
      const client = clientRef.current;
      const snapshot = items ?? mentionsFeed;
      const clearedIds = new Set(snapshot.map((item) => item.eventId));
      const newestByRoom = new Map<string, MentionFeedItem>();
      for (const item of snapshot) {
        const current = newestByRoom.get(item.roomId);
        if (!current || item.ts > current.ts) newestByRoom.set(item.roomId, item);
      }
      if (client) {
        await Promise.all(
          Array.from(newestByRoom.values()).map(async (item) => {
            await matrixSendReadReceipt(client, item.roomId, item.eventId);
          }),
        );
      }
      for (const item of snapshot) clearedMentionIdsRef.current.add(item.eventId);
      for (const item of newestByRoom.values()) {
        const current = clearedMentionWatermarksRef.current.get(item.roomId);
        if (current === undefined || item.ts > current) {
          clearedMentionWatermarksRef.current.set(item.roomId, item.ts);
        }
      }
      setMentionsFeed((current) =>
        current.filter((item) => {
          if (clearedIds.has(item.eventId)) return false;
          const watermark = clearedMentionWatermarksRef.current.get(item.roomId);
          return watermark === undefined || item.ts > watermark;
        }),
      );
    },
    [mentionsFeed],
  );

  const jumpToEvent = useCallback(
    ({ roomId, eventId }: { roomId: string; eventId: string }) => {
      if (activeRoomIdRef.current !== roomId) setActiveThreadRoot(null);
      activeRoomIdRef.current = roomId;
      activeRoomOriginRef.current = "user";
      persistActiveRoomId(roomId);
      const myToken = jumpTokenRef.current + 1;
      jumpTokenRef.current = myToken;
      setActiveRoomIdState(roomId);
      setActiveViewState("room");
      // Race: the render-time jump effect also paginates prevBatchByRoom,
      // so publish pendingJump only after the binding walk settles — or the
      // two co-paginate the shared cursor into disjoint, misordered windows.
      const applyJump = () => {
        if (jumpTokenRef.current !== myToken || activeRoomIdRef.current !== roomId) return;
        setPendingJump({ roomId, eventId, token: myToken });
      };
      const load = clientRef.current?.bindings.jumpToEvent(roomId, eventId);
      if (!load) {
        applyJump();
        return;
      }
      load
        .catch((err: unknown) => {
          console.warn("jumpToEvent", err);
        })
        .finally(applyJump);
    },
    [persistActiveRoomId],
  );

  const clearPendingJump = useCallback(() => {
    setPendingJump(null);
  }, []);

  const openThread = useCallback(({ roomId, root }: { roomId: string; root: TimelineEvent }) => {
    // Replace any currently-open thread (single-slot). Storing the
    // full event spares the panel from a `getEvent` round-trip on
    // open — the timeline already has the event in memory and is
    // the only realistic opener.
    setActiveThreadRoot({ roomId, root });
  }, []);

  const closeThread = useCallback(() => {
    setActiveThreadRoot(null);
  }, []);

  const setChannelType = useCallback(async (roomId: string, type: ChannelType): Promise<void> => {
    const client = clientRef.current;
    if (!client) {
      throw new Error("setChannelType called before Matrix client is ready");
    }
    await matrixSetChannelType(client, roomId, type);
  }, []);

  const leaveRoom = useCallback(async (roomId: string): Promise<void> => {
    const client = clientRef.current;
    if (!client) {
      throw new Error("leaveRoom called before Matrix client is ready");
    }
    await matrixLeaveRoom(client, roomId);
    if (activeRoomIdRef.current === roomId) {
      activeRoomIdRef.current = null;
      activeRoomOriginRef.current = null;
      setActiveRoomIdState(null);
      setPendingJump(null);
      setActiveThreadRoot(null);
      setActiveViewState("room");
    }
  }, []);

  const dismissRecoveryKey = useCallback(() => {
    setPendingRecoveryKey(null);
  }, []);

  const markRecovered = useCallback(() => {
    clearSkippedRecovery();
    setNeedsRecovery(false);
  }, [clearSkippedRecovery]);

  const skipRecoveryForSession = useCallback(() => {
    const currentUserId = currentRecoveryUserId();
    if (currentUserId) writeRecoverySkip(currentUserId);
    setSkippedRecovery(true);
    setNeedsRecovery(false);
  }, [currentRecoveryUserId]);

  // Depending on `client` here is deliberate: when the client finishes
  // booting the callback identity changes and `useThread` re-runs its
  // effect, upgrading from the empty no-op snapshot to a real
  // subscription. Without this, a ThreadPanel mounted pre-boot would
  // stay permanently empty.
  const subscribeThread = useCallback(
    (roomId: string, rootEventId: string, cb: ThreadSubscriber): Unsubscribe => {
      if (!client) {
        // Boot races: a ThreadPanel may mount before `createClient`
        // resolves. Emit an empty snapshot so the loading state
        // resolves cleanly, then no-op the unsubscribe. The callback
        // identity flips when `client` lands, which re-runs the
        // `useThread` effect and installs the live subscription.
        cb([]);
        return () => {};
      }
      return matrixSubscribeThread(client, roomId, rootEventId, cb);
    },
    [client],
  );

  const sendThreadReply = useCallback(
    async (
      roomId: string,
      rootEventId: string,
      body: string,
      latestReplyId?: string,
      options?: ThreadReplyOptions,
    ): Promise<string> => {
      if (!client) {
        throw new Error("sendThreadReply called before Matrix client is ready");
      }
      if (options === undefined) {
        return matrixSendThreadReply(client, roomId, rootEventId, body, latestReplyId);
      }
      return matrixSendThreadReply(client, roomId, rootEventId, body, latestReplyId, options);
    },
    [client],
  );

  const recoveryView = useMemo(
    () =>
      needsRecovery
        ? buildRecoveryViewModel({
            devices: recoveryDevices,
            skipped: skippedRecovery,
          })
        : null,
    [needsRecovery, recoveryDevices, skippedRecovery],
  );

  const [blockedUserIds, setBlockedUserIds] = useState<string[]>([]);
  useEffect(() => {
    if (!client || !userId) {
      setBlockedUserIds([]);
      return;
    }
    return subscribeBlockedUsers(client, setBlockedUserIds);
  }, [client, userId]);
  const visibleRooms = useMemo(
    () => withoutBlockedDirects(rooms, blockedUserIds),
    [rooms, blockedUserIds],
  );

  const value = useMemo<MatrixContextValue>(
    () => ({
      state,
      lastError,
      displayName,
      userId,
      client,
      roomsReady,
      activeOrgSpaceId,
      setActiveOrgSpaceId,
      activeSpaceId,
      setActiveSpaceId,
      activeRoomId,
      setActiveRoomId,
      activeView,
      setActiveView,
      savedItems,
      saveEvent,
      unsaveEvent,
      clearSaved,
      unreadGroups,
      mentionsFeed,
      clearMentions,
      jumpToEvent,
      pendingJump,
      clearPendingJump,
      cryptoReady,
      cryptoError,
      forceReloginBanner,
      keychainError,
      retryKeychain,
      retryCryptoBootstrap,
      needsRecovery: needsRecovery && !skippedRecovery,
      recoveryView,
      pendingRecoveryKey,
      dismissRecoveryKey,
      markRecovered,
      skipRecoveryForSession,
      canUnlockHistory: needsRecovery,
      resumeRecovery: clearSkippedRecovery,
      signIn,
      signOut,
      registerSignOutTeardown,
      recoverToLogin,
      completeLogin,
      createRoom,
      createSpace,
      moveRoomToSpace,
      addDirectRoom,
      createDirectRoom,
      findDirectRoom,
      searchUserDirectory,
      searchRooms,
      setChannelType,
      leaveRoom,
      subscribeThread,
      sendThreadReply,
      activeThreadRoot,
      openThread,
      closeThread,
    }),
    [
      state,
      lastError,
      displayName,
      userId,
      client,
      roomsReady,
      activeOrgSpaceId,
      setActiveOrgSpaceId,
      activeSpaceId,
      setActiveSpaceId,
      activeRoomId,
      setActiveRoomId,
      activeView,
      setActiveView,
      savedItems,
      saveEvent,
      unsaveEvent,
      clearSaved,
      unreadGroups,
      mentionsFeed,
      clearMentions,
      jumpToEvent,
      pendingJump,
      clearPendingJump,
      cryptoReady,
      cryptoError,
      forceReloginBanner,
      keychainError,
      retryKeychain,
      retryCryptoBootstrap,
      needsRecovery,
      skippedRecovery,
      recoveryView,
      pendingRecoveryKey,
      dismissRecoveryKey,
      markRecovered,
      skipRecoveryForSession,
      clearSkippedRecovery,
      signIn,
      signOut,
      registerSignOutTeardown,
      recoverToLogin,
      completeLogin,
      createRoom,
      createSpace,
      moveRoomToSpace,
      addDirectRoom,
      createDirectRoom,
      findDirectRoom,
      searchUserDirectory,
      searchRooms,
      setChannelType,
      leaveRoom,
      subscribeThread,
      sendThreadReply,
      activeThreadRoot,
      openThread,
      closeThread,
    ],
  );

  return (
    <MatrixContext.Provider value={value}>
      <MatrixRoomsContext.Provider value={visibleRooms}>{children}</MatrixRoomsContext.Provider>
      {pendingUiaRequest && (
        <UiaDialog
          flow={pendingUiaRequest.flow}
          onSubmit={pendingUiaRequest.onSubmit}
          onOpen={pendingUiaRequest.onOpen}
          onCancel={pendingUiaRequest.onCancel}
        />
      )}
    </MatrixContext.Provider>
  );
}
